import { Component, Inject, LOCALE_ID, OnInit } from "@angular/core";
import { UntypedFormBuilder, UntypedFormGroup, Validators, UntypedFormArray } from "@angular/forms";
import { MatDialogRef, MAT_DIALOG_DATA } from "@angular/material/dialog";
import { FuseTranslationLoaderService } from "@fuse/services/translation-loader.service";
import { AlarmModelThreshold, AlarmModelTimeWindow, AlarmModel, AlarmPeriodLimitModel } from "app/models/alarm.model";
import { EventModelSeverity } from "app/models/event.model";
import { EventComponent } from "app/modules/events/event/event.component";
import { locale as catalan } from "../../modules/devices/i18n/ca";
import { locale as english } from "../../modules/devices/i18n/en";
import { locale as spanish } from "../../modules/devices/i18n/es";
import { locale as adminCatalan } from "../../modules/admin/i18n/ca";
import { locale as adminEnglish } from "../../modules/admin/i18n/en";
import { locale as adminSpanish } from "../../modules/admin/i18n/es";
import { ProjectDeviceModel } from "app/models/projectDevice.model";
import { DataTypeModelKey } from "../../models/dataType.model";
import { ProjectModel } from "../../models/project.model";
import { ProjectAlarmModel } from "../../models/projectAlarm.model";
import { ProjectDeviceService } from "../../services/api/projectDevice.service";
import { AuthenticationService } from "app/services/authentication.service";
import { UserModel } from "app/models/user.model";
import { ProjectService } from "app/services/api/project.service";
import { getProjectPeriodSegments } from "app/shared/project-periods.core";
import { cloneAlarmForForm } from "app/shared/alarm-duplication";
import { NotificationChannelModel } from "app/models/notificationChannel.model";

export enum AlarmAction {
    NEW = "NEW",
    EDIT = "EDIT",
}

enum AlarmType {
    DEVICE = "DEVICE",
    PROJECT = "PROJECT",
}

enum AlarmLimitMode {
    WholeDay = "whole_day",
    Periods = "periods",
}

@Component({
    selector: "app-alarm-add-edit",
    templateUrl: "./alarm-add-edit.component.html",
    styleUrls: ["./alarm-add-edit.component.scss"],
    standalone: false,
})
export class AlarmAddEditComponent implements OnInit {
    private readonly prioritizedAlarmFields: DataTypeModelKey[] = [
        DataTypeModelKey.soundPressureLevel,
        DataTypeModelKey.soundPressureLevelC,
        DataTypeModelKey.rolling5s,
        DataTypeModelKey.rolling5sC,
        DataTypeModelKey.rolling5,
        DataTypeModelKey.rolling5C,
        DataTypeModelKey.rolling30,
        DataTypeModelKey.rolling30C,
        DataTypeModelKey.soundPressureLevelCMinusA,
    ];
    windowValues = [AlarmModelTimeWindow.AlarmModelTimeWindowInstant, AlarmModelTimeWindow.AlarmModelTimeWindowBucket5];
    severityValues = [
        EventModelSeverity.EventModelSeverityInfo,
        EventModelSeverity.EventModelSeveritySuccess,
        EventModelSeverity.EventModelSeverityWarning,
        EventModelSeverity.EventModelSeverityAlarm,
    ];
    thresholdValues = [AlarmModelThreshold.AlarmModelThresholdCrossingBelow, AlarmModelThreshold.AlarmModelThresholdCrossingOver];

    user: UserModel;

    public alarmForm: UntypedFormGroup;
    public action: AlarmAction;
    public alarmType: AlarmType;
    public device: ProjectDeviceModel;
    public alarm: AlarmModel | ProjectAlarmModel = new AlarmModel();
    public project: ProjectModel;
    public alarmFields: DataTypeModelKey[];
    public projectDevices: ProjectDeviceModel[] = [];
    public availableNotificationChannels: NotificationChannelModel[] = [];
    readonly alarmLimitMode = AlarmLimitMode;

    constructor(
        @Inject(MAT_DIALOG_DATA) private _data: any,
        private formBuilder: UntypedFormBuilder,
        public matDialogRef: MatDialogRef<EventComponent>,
        private _fuseTranslationLoaderService: FuseTranslationLoaderService,
        private projectDeviceService: ProjectDeviceService,
        private authenticationService: AuthenticationService,
        private projectService: ProjectService,
        @Inject(LOCALE_ID) private locale: string,
    ) {
        this._fuseTranslationLoaderService.loadTranslations(english, spanish, catalan, adminEnglish, adminSpanish, adminCatalan);

        if (_data.alarm) {
            this.action = _data.action ?? AlarmAction.EDIT;
            this.alarm = cloneAlarmForForm(_data.alarm);
        } else {
            this.action = _data.action ?? AlarmAction.NEW;
        }

        if (_data.device) {
            this.device = _data.device;
            this.alarmType = AlarmType.DEVICE;
        } else if (_data.project) {
            this.project = _data.project;
            this.alarmType = AlarmType.PROJECT;
            this.availableNotificationChannels = this.project.notificationChannels ?? [];
        }

        this.alarmFields = this.sortAlarmFields(
            this.alarmType == AlarmType.DEVICE
                ? this.device.fields.filter((f) => f !== DataTypeModelKey.spectrum13Octave)
                : Object.values(DataTypeModelKey),
        );

        this.user = authenticationService.currentUser;
        this.createForm();
    }

    ngOnInit() {
        this.resolveProjectNotificationChannels();

        if (this.alarmType === AlarmType.PROJECT) {
            this.loadProjectDevices();
        }

        this.syncPeriodLimitsForm();
    }

    private resolveProjectNotificationChannels() {
        const projectId = this.alarmType === AlarmType.PROJECT ? this.project?.id : this.device?.project;

        if (!projectId) {
            return;
        }

        if (this.project) {
            this.availableNotificationChannels = this.project.notificationChannels ?? [];
        }

        this.projectService.get(projectId).subscribe(
            (project) => {
                this.project = project;
                this.availableNotificationChannels = project?.notificationChannels ?? [];
                this.syncPeriodLimitsForm();
            },
            (error) => console.log(error),
        );
    }

    private loadProjectDevices() {
        this.projectDeviceService.getAll(this.project.id).subscribe(
            (devices) => (this.projectDevices = devices),
            (error) => console.log(error),
        );
    }

    private createForm(): any {
        if (!this.alarm.notificationChannelIds) {
            this.alarm.notificationChannelIds = [];
        }
        const ignoredDevices =
            this.alarmType === AlarmType.PROJECT && Array.isArray((this.alarm as ProjectAlarmModel).ignoredDevices)
                ? [...(this.alarm as ProjectAlarmModel).ignoredDevices]
                : [];

        const formFields = {
            name: [this.alarm.name, [Validators.required]],
            description: [this.alarm.description],
            field: [this.alarm.field, [Validators.required]],
            window: [this.alarm.window, [Validators.required]],
            threshold: [this.alarm.threshold, [Validators.required]],
            limit: [this.alarm.limit, [Validators.required]],
            limitMode: [this.alarm.periodLimits?.length ? AlarmLimitMode.Periods : AlarmLimitMode.WholeDay],
            periodLimits: this.formBuilder.array([]),
            severity: [this.alarm.severity, [Validators.required]],
            notify: [this.alarm.notify, [Validators.required]],
            notificationChannelIds: [this.alarm.notificationChannelIds, [Validators.nullValidator]],
            silent: [this.alarm.silent, [Validators.required]],
            needACK: [this.alarm.needACK],
            ignoredDevices: [ignoredDevices],
        };

        this.alarmForm = this.formBuilder.group(formFields);
        this.alarmForm.get("limitMode").valueChanges.subscribe(() => this.syncPeriodLimitsForm());
        this.syncPeriodLimitsForm();
    }

    get periodLimitsFormArray(): UntypedFormArray {
        return this.alarmForm.get("periodLimits") as UntypedFormArray;
    }

    get hasProjectPeriods(): boolean {
        return !!this.project?.periods?.length;
    }

    get projectPeriodsResolved(): boolean {
        return Array.isArray(this.project?.periods);
    }

    get usesPeriodLimits(): boolean {
        return this.hasProjectPeriods && this.alarmForm?.get("limitMode")?.value === AlarmLimitMode.Periods;
    }

    get hasProjectNotificationChannels(): boolean {
        return (this.availableNotificationChannels?.length ?? 0) > 0;
    }

    getPeriodIntervalsLabel(periodName: string): string {
        const period = this.project?.periods?.find((candidate) => candidate.name === periodName);
        if (!period) {
            return "";
        }

        return getProjectPeriodSegments(period)
            .map((segment) => `${this.formatMinute(segment.start)} - ${this.formatSegmentEnd(segment.start, segment.end)}`)
            .join(", ");
    }

    private syncPeriodLimitsForm() {
        if (!this.alarmForm) {
            return;
        }

        const limitField = this.alarmForm.get("limit");
        const limitModeField = this.alarmForm.get("limitMode");
        while (this.periodLimitsFormArray.length > 0) {
            this.periodLimitsFormArray.removeAt(0);
        }

        if (!this.hasProjectPeriods) {
            if (this.projectPeriodsResolved) {
                limitModeField.setValue(AlarmLimitMode.WholeDay, { emitEvent: false });
            }
            limitField.setValidators([Validators.required]);
            limitField.updateValueAndValidity();
            return;
        }

        if (limitModeField.value === AlarmLimitMode.WholeDay) {
            limitField.setValidators([Validators.required]);
            limitField.updateValueAndValidity();
            return;
        }

        const existingLimits = new Map<string, AlarmPeriodLimitModel>(
            (this.alarm.periodLimits ?? []).map((periodLimit) => [periodLimit.periodName, periodLimit]),
        );
        this.project.periods.forEach((period) => {
            this.periodLimitsFormArray.push(
                this.formBuilder.group({
                    periodName: [{ value: period.name, disabled: true }],
                    limit: [existingLimits.get(period.name)?.limit ?? this.alarm.limit, [Validators.required]],
                }),
            );
        });

        limitField.clearValidators();
        limitField.updateValueAndValidity();
    }

    public save() {
        const alarm = {
            ...this.alarm,
            ...this.alarmForm.getRawValue(),
        };
        if (this.action === AlarmAction.EDIT) {
            alarm.id = this.alarm.id;
        }
        alarm.ignoredDevices = Array.isArray(alarm.ignoredDevices) ? alarm.ignoredDevices : [];
        alarm.notificationChannelIds = Array.isArray(alarm.notificationChannelIds) ? alarm.notificationChannelIds : [];
        if (this.usesPeriodLimits) {
            alarm.periodLimits = this.periodLimitsFormArray.getRawValue();
            alarm.limit = alarm.periodLimits[0].limit;
        } else {
            alarm.periodLimits = [];
        }
        delete alarm.limitMode;
        this.matDialogRef.close({
            alarm,
        });
    }

    public cancel() {
        this.matDialogRef.close();
    }

    private sortAlarmFields(fields: DataTypeModelKey[]): DataTypeModelKey[] {
        const priorityOrder = new Map(this.prioritizedAlarmFields.map((field, index) => [field, index]));

        return [...fields].sort((left, right) => {
            const leftPriority = priorityOrder.get(left);
            const rightPriority = priorityOrder.get(right);

            if (leftPriority !== undefined || rightPriority !== undefined) {
                if (leftPriority === undefined) {
                    return 1;
                }
                if (rightPriority === undefined) {
                    return -1;
                }
                return leftPriority - rightPriority;
            }

            return this._fuseTranslationLoaderService.translateService
                .instant(`FIELD.${left}`)
                .localeCompare(this._fuseTranslationLoaderService.translateService.instant(`FIELD.${right}`), this.locale);
        });
    }

    private formatMinute(totalMinutes: number): string {
        const normalized = ((totalMinutes % (24 * 60)) + 24 * 60) % (24 * 60);
        const hours = Math.floor(normalized / 60)
            .toString()
            .padStart(2, "0");
        const minutes = (normalized % 60).toString().padStart(2, "0");
        return `${hours}:${minutes}`;
    }

    private formatSegmentEnd(startMinute: number, endMinute: number): string {
        if (startMinute > 0 && endMinute === 24 * 60) {
            return "24:00";
        }

        return this.formatMinute(endMinute);
    }
}
